/**
 * The loading overlay, switched on by BUILD_SETTINGS `loadingOverlay`. A prerendered page
 * paints before its JS has hydrated it and before it has settled (a returning visitor's saved
 * answers merged in, an order restored), so a tap in that window does nothing or the wrong
 * thing. The overlay covers the page from the first paint until it is ready to use.
 *
 * How it gets there and goes away:
 *  - loadingOverlay.nitro.ts makes `loadingOverlayHtml()` the first thing in every page's
 *    <body>, ahead of `#__nuxt`: static markup Vue never hydrates. nuxt.config.ts registers
 *    that plugin only when the setting is on, so a build without it has no overlay at all.
 *  - app.vue calls `hideLoadingOverlay()` after `pipe.whenReady()` and a tick, not at
 *    hydration: the saved-answers merge runs a `setTimeout(0)` later and re-renders the page,
 *    and whenReady() also waits for the blocking integrations.
 *  - When the app never takes it away (the bundle failed to load, the app crashed), its own
 *    inline script does, GIVE_UP_MS after the page's `load` event. Never a timer from the first
 *    paint: no fixed time tells a slow network from a dead bundle, and on Slow 3G the bundle
 *    alone takes longer than 8 s to arrive. With JS off the <noscript> hides it, and if the
 *    inline script cannot run, the CSS give-up animation hides it GIVE_UP_MS after it appeared.
 *
 * What a visitor sees (the CSS and hideLoadingOverlay share these timings):
 *  - for SHOW_DELAY_MS nothing: the overlay is transparent but already catches taps;
 *  - then the scrim (the page's background at 60 % over a 3 px blur of the page) fades in, and
 *    the card with the ring in the page's text colour pops in 60 ms after it;
 *  - once the page is ready, taps reach it. An overlay that never showed goes at once; one
 *    that did stays until it has shown for MIN_VISIBLE_MS, so it never flickers, then fades
 *    out over FADE_OUT_MS.
 *
 * Rules for whoever restyles it (a redesign lands in this file):
 *  - Plain CSS only: nothing lowers it for the older-browser build (Chrome 64, Safari 12).
 *    Full screen is `position: fixed` with top/right/bottom/left 0, never `inset`, and
 *    z-index 2147483647. No color-mix(), which is why the scrim tint is a layer of its own.
 *  - `px`, not `rem`: a pipe may scale the root font size (a responsive layout does). The
 *    overlay sits outside the zoomed `.body-content` wrapper, so the page zoom never reaches
 *    it; only the iOS fallback, a `width=<design>` viewport (render/lib/pageZoom.ts), scales
 *    it along with the whole page.
 *  - No JS animation (no JS has loaded while it is on screen) and no external files: inline
 *    SVG and CSS only, so it is there in the first frame. Animate `opacity` and `transform`
 *    only: they keep running on the compositor while hydration blocks the main thread. The
 *    inline script only times the overlay, in ES5: nothing lowers it either.
 *  - The pipe's theme variables are already defined (its stylesheet is inlined in <head>),
 *    so `var(--token, fallback)` gives each brand its own colours; `--hl-scrim`,
 *    `--hl-surface` and `--hl-accent` let a stylesheet override the overlay's. Visible text
 *    needs a system-font fallback: the brand font may still be loading.
 *  - `touch-action: none` on the overlay, so touch gestures do not scroll the covered page,
 *    and `pointer-events: none` once the page is ready. Never lock `html`/`body` overflow:
 *    anything only JS can undo stays stuck when the bundle fails.
 *  - Every selector starts with the overlay's id and every keyframe name with `plo-`: the
 *    overlay's CSS reaches nothing else, and the id outranks the page's own element rules.
 *  - Keep it small: it ships in every page's HTML.
 */

/** The overlay element: app.vue takes it away, and every selector below starts with it. */
export const LOADING_OVERLAY_ID = 'pipe-loading-overlay'

/** Until then the overlay only catches taps: a page ready that fast never shows it. */
const SHOW_DELAY_MS = 300
/** Once drawn, it stays at least this long. */
const MIN_VISIBLE_MS = 400
const FADE_OUT_MS = 200
/** The failsafe: how long after the page's `load` the overlay still waits for the app. */
const GIVE_UP_MS = 8000
/**
 * The fallback give-up, for when the inline script cannot run; the stamp the script puts on
 * the overlay stops it. Its keyframes hide at `from` as well as `to`: the 1 ms animation
 * finishes at a progress of 0.9999…, and `visibility` only turns hidden at exactly 1.
 */
const GIVE_UP_ANIMATION = 'plo-give-up'
/** When the overlay appeared (`performance.now()`), stamped by its inline script. */
const SINCE_ATTRIBUTE = 'data-since'

/**
 * Runs as the overlay is parsed, so it needs no bundle: stamps the overlay, and removes it
 * GIVE_UP_MS after `load`. That event waits for every script the HTML names, the bundle among
 * them, to run or fail, so a page that loads slowly keeps its overlay as long as it needs.
 */
const SCRIPT =
  `(function(o){o.setAttribute('${SINCE_ATTRIBUTE}',performance.now());` +
  `addEventListener('load',function(){setTimeout(function(){o.remove()},${GIVE_UP_MS})})})` +
  `(document.currentScript.parentNode)`

const CSS = `
#${LOADING_OVERLAY_ID} {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2147483647;
  touch-action: none;
  cursor: progress;
  animation: ${GIVE_UP_ANIMATION} 1ms ${GIVE_UP_MS}ms forwards;
}
#${LOADING_OVERLAY_ID}[${SINCE_ATTRIBUTE}] {
  animation: none;
}
#${LOADING_OVERLAY_ID}[data-state] {
  pointer-events: none;
}
#${LOADING_OVERLAY_ID}::before,
#${LOADING_OVERLAY_ID}::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  animation: plo-in 240ms ease-out ${SHOW_DELAY_MS}ms both;
}
#${LOADING_OVERLAY_ID}::before {
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
}
#${LOADING_OVERLAY_ID}::after {
  background: var(--hl-scrim, var(--color-background-page, #fff));
  opacity: 0.6;
}
#${LOADING_OVERLAY_ID} > div {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  width: 64px;
  height: 64px;
  margin: -32px 0 0 -32px;
  border-radius: 16px;
  background: var(--hl-surface, var(--color-background-surface-raised, #fff));
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.12);
  animation: plo-pop 240ms cubic-bezier(0.34, 1.56, 0.64, 1) ${SHOW_DELAY_MS + 60}ms both;
}
#${LOADING_OVERLAY_ID}[data-state='out']::before,
#${LOADING_OVERLAY_ID}[data-state='out']::after,
#${LOADING_OVERLAY_ID}[data-state='out'] > div {
  animation: plo-out ${FADE_OUT_MS}ms ease-out forwards;
}
#${LOADING_OVERLAY_ID} svg {
  display: block;
  position: absolute;
  top: 16px;
  left: 16px;
  width: 32px;
  height: 32px;
  fill: none;
  stroke: var(--hl-accent, currentColor);
  stroke-width: 3.5px;
  stroke-linecap: round;
  animation: plo-spin 1.4s linear infinite;
}
#${LOADING_OVERLAY_ID} circle:first-child {
  opacity: 0.14;
}
#${LOADING_OVERLAY_ID} circle + circle {
  stroke-dasharray: 59 31;
}
#${LOADING_OVERLAY_ID} > span {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
@media (prefers-reduced-motion: reduce) {
  #${LOADING_OVERLAY_ID} > div {
    animation-name: plo-in;
  }
  #${LOADING_OVERLAY_ID} svg {
    animation: plo-pulse 1.4s ease-in-out infinite alternate;
  }
}
@keyframes ${GIVE_UP_ANIMATION} {
  from,
  to {
    visibility: hidden;
  }
}
@keyframes plo-in {
  from {
    opacity: 0;
  }
}
@keyframes plo-pop {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
}
@keyframes plo-out {
  to {
    opacity: 0;
  }
}
@keyframes plo-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes plo-pulse {
  from {
    opacity: 0.4;
  }
}
`

/** A 70 % arc over a full track, both drawn by the CSS above. */
const RING =
  '<svg viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="16" r="14.25"/><circle cx="16" cy="16" r="14.25"/></svg>'

/** The overlay's only words, for screen readers, by the page's language: English when it is not here. */
const LOADING_LABELS: Record<string, string> = {
  ar: 'جارٍ التحميل',
  bg: 'Зареждане',
  bs: 'Učitavanje',
  ca: 'Carregant',
  cs: 'Načítání',
  da: 'Indlæser',
  de: 'Wird geladen',
  el: 'Φόρτωση',
  en: 'Loading',
  es: 'Cargando',
  et: 'Laadimine',
  fi: 'Ladataan',
  fr: 'Chargement',
  he: 'טוען',
  hi: 'लोड हो रहा है',
  hr: 'Učitavanje',
  hu: 'Betöltés',
  id: 'Memuat',
  it: 'Caricamento',
  ja: '読み込み中',
  ko: '로드 중',
  lt: 'Įkeliama',
  lv: 'Ielādē',
  ms: 'Memuatkan',
  nb: 'Laster inn',
  nl: 'Laden',
  no: 'Laster inn',
  pl: 'Ładowanie',
  pt: 'Carregando',
  ro: 'Se încarcă',
  ru: 'Загрузка',
  sk: 'Načítava sa',
  sl: 'Nalaganje',
  sr: 'Учитавање',
  sv: 'Laddar',
  th: 'กำลังโหลด',
  tr: 'Yükleniyor',
  uk: 'Завантаження',
  vi: 'Đang tải',
  zh: '加载中',
}

/**
 * The overlay's markup for a page in `lang` (its <html lang>): the style, the <noscript> and
 * the element, with its script inside so that it goes along with it.
 */
export function loadingOverlayHtml(lang?: string): string {
  const label = LOADING_LABELS[(lang ?? '').toLowerCase().split('-')[0]] ?? LOADING_LABELS.en
  return (
    `<style>${CSS.trim().replace(/\s*\n\s*/g, ' ')}</style>` +
    `<noscript><style>#${LOADING_OVERLAY_ID}{display:none}</style></noscript>` +
    `<div id="${LOADING_OVERLAY_ID}" role="status"><div>${RING}</div><span>${label}</span>` +
    `<script>${SCRIPT}</script></div>`
  )
}

/**
 * Takes the overlay away once the page is ready to use. Does nothing in a build without one.
 *
 * Only the element goes. The <style> and <noscript> ahead of it are the first nodes of <body>,
 * which Vue keeps as anchors for a `<Teleport to="body">` that hydrated disabled
 * (SplitBackground, StickyContainer): removing them would break that teleport.
 */
export function hideLoadingOverlay(): void {
  const overlay = document.getElementById(LOADING_OVERLAY_ID)
  if (!overlay) return
  // How long it has been drawn, read off its stamp. Without one (its script could not run)
  // there is no telling, so it counts as long enough.
  const since = overlay.getAttribute(SINCE_ATTRIBUTE)
  const shownFor = since ? performance.now() - Number(since) - SHOW_DELAY_MS : MIN_VISIBLE_MS
  if (shownFor < 0) {
    overlay.remove()
    return
  }
  overlay.dataset.state = 'ready'
  setTimeout(
    () => {
      overlay.dataset.state = 'out'
      setTimeout(() => overlay.remove(), FADE_OUT_MS)
    },
    Math.max(0, MIN_VISIBLE_MS - shownFor),
  )
}
